import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api, type OrderQuery } from '../api/client';
import type { MetaResponse, OrdersResponse, SummaryResponse } from '../api/types';

/** Debounce a rapidly-changing value — used so typing does not hit the API per keystroke. */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
}

const initial = <T,>(): AsyncState<T> => ({ data: null, loading: true, error: null });

/**
 * TWO KINDS OF LOADING
 * --------------------
 * The distinction matters more than it looks:
 *
 *  - **The query changed** (date range, status filter, search, page). Whatever
 *    is on screen now answers a *different question*, so it must be cleared and
 *    replaced with skeletons. Leaving the old numbers up for the 2-3 s a wide
 *    range takes is actively misleading — the operator cannot tell whether the
 *    new figures have landed.
 *
 *  - **A refresh of the same query** (auto-refresh, or the Refresh button). The
 *    data on screen still answers the current question; it is merely a few
 *    seconds old. Blanking it would make the dashboard flicker every interval,
 *    so it stays put and freshness is signalled by the "Updated HH:MM" stamp.
 *
 * Both hooks below implement that rule by comparing the query key, ignoring the
 * refresh counter.
 */

/** Warehouse identity + the live status catalogue. Fetched once. */
export function useMeta(): AsyncState<MetaResponse> & { retry: () => void } {
  const [state, setState] = useState<AsyncState<MetaResponse>>(initial);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    api
      .meta(ac.signal)
      .then((data) => setState({ data, loading: false, error: null }))
      .catch((err: unknown) => {
        if ((err as Error).name === 'AbortError') return;
        setState({ data: null, loading: false, error: asApiError(err) });
      });
    return () => ac.abort();
  }, [nonce]);

  return { ...state, retry: useCallback(() => setNonce((n) => n + 1), []) };
}

/** KPI cards + status distribution for the selected range. */
export function useSummary(from: string, to: string, refreshKey: number): AsyncState<SummaryResponse> {
  const [state, setState] = useState<AsyncState<SummaryResponse>>(initial);
  const queryKey = `${from}|${to}`;
  const prevQueryKey = useRef<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    const isNewQuery = prevQueryKey.current !== queryKey;
    prevQueryKey.current = queryKey;

    // New question -> drop the old answer so skeletons take over.
    setState((s) => ({ data: isNewQuery ? null : s.data, loading: true, error: null }));

    api
      .summary(from, to, ac.signal)
      .then((data) => setState({ data, loading: false, error: null }))
      .catch((err: unknown) => {
        if ((err as Error).name === 'AbortError') return;
        setState({ data: null, loading: false, error: asApiError(err) });
      });
    return () => ac.abort();
  }, [from, to, queryKey, refreshKey]);

  return state;
}

/** Paginated orders table. */
export function useOrders(query: OrderQuery, refreshKey: number): AsyncState<OrdersResponse> {
  const [state, setState] = useState<AsyncState<OrdersResponse>>(initial);
  const keep = useRef<OrdersResponse | null>(null);

  const queryKey = useMemo(
    () =>
      JSON.stringify([
        query.from,
        query.to,
        [...(query.statuses ?? [])].sort(),
        query.search ?? '',
        query.page ?? 1,
        query.pageSize ?? 25,
        query.sortBy ?? 'order_date',
        query.sortDir ?? 'desc',
      ]),
    [query],
  );
  const prevQueryKey = useRef<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    const isNewQuery = prevQueryKey.current !== queryKey;
    prevQueryKey.current = queryKey;

    if (isNewQuery) keep.current = null;
    setState({ data: keep.current, loading: true, error: null });

    api
      .orders(query, ac.signal)
      .then((data) => {
        keep.current = data;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if ((err as Error).name === 'AbortError') return;
        keep.current = null;
        setState({ data: null, loading: false, error: asApiError(err) });
      });
    return () => ac.abort();
    // `queryKey` is the stable serialisation of `query`; `refreshKey` forces a
    // refetch of the same query without clearing what is on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, refreshKey]);

  return state;
}

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError
    ? err
    : new ApiError('UNKNOWN', 'Something went wrong while loading dashboard data.', 0);
}
